/**
 * Local browser notifications for team activity.
 *
 * Strategy (Feb 18, 2026):
 *   - Lightweight client-side polling: when in team scope, periodically
 *     hit /api/teams/{id}/todos and compare against the last known set.
 *     New items → fire a local Notification.
 *   - No backend push / VAPID required for v1. Works for the PWA installed
 *     on desktop today. When the tab is in the background (or PWA window
 *     not focused), the OS notification still surfaces because we ask the
 *     service worker to showNotification() via the existing /sw.js.
 *   - Provider explicitly opts in via the "Enable browser notifications"
 *     toggle in the To-Do header — we never silently ask for permission.
 *   - Once granted, we cache the last-seen-id set per-team in localStorage
 *     so reload doesn't fire a notification storm.
 */

const LAST_SEEN_KEY = (teamId) => `writeright_todo_last_seen_${teamId}`;
const PERM_ASKED_KEY = 'writeright_notif_perm_asked';
const ENABLED_KEY = 'writeright_notif_enabled';

export const notifSupported = () => {
  return typeof window !== 'undefined'
    && 'Notification' in window
    && 'serviceWorker' in navigator;
};

export const notifPermission = () => {
  if (!notifSupported()) return 'unsupported';
  return Notification.permission;  // 'granted' | 'denied' | 'default'
};

export const notifEnabled = () => {
  return localStorage.getItem(ENABLED_KEY) === 'true' && notifPermission() === 'granted';
};

export const requestNotifPermission = async () => {
  if (!notifSupported()) return 'unsupported';
  if (Notification.permission === 'granted') {
    localStorage.setItem(ENABLED_KEY, 'true');
    return 'granted';
  }
  if (Notification.permission === 'denied') return 'denied';
  const result = await Notification.requestPermission();
  localStorage.setItem(PERM_ASKED_KEY, 'true');
  if (result === 'granted') {
    localStorage.setItem(ENABLED_KEY, 'true');
  }
  return result;
};

export const disableNotifications = () => {
  localStorage.setItem(ENABLED_KEY, 'false');
};

const showLocalNotification = async (title, body, data = {}) => {
  if (!notifEnabled()) return;
  try {
    const reg = await navigator.serviceWorker.getRegistration();
    if (reg && reg.showNotification) {
      // Service worker route — fires even if app tab is backgrounded.
      reg.showNotification(title, {
        body,
        icon: '/icons/icon-192.png',
        badge: '/icons/icon-192.png',
        tag: data.tag || 'writeright-team',
        data,
        requireInteraction: false,
      });
      return;
    }
  } catch (_) { /* fall through */ }
  // Fallback: foreground-only Notification
  try {
    new Notification(title, { body, icon: '/icons/icon-192.png', tag: data.tag });
  } catch (_) { /* swallow */ }
};

const loadLastSeen = (teamId) => {
  try {
    const raw = localStorage.getItem(LAST_SEEN_KEY(teamId));
    if (!raw) return new Set();
    const parsed = JSON.parse(raw);
    return new Set(Array.isArray(parsed) ? parsed : []);
  } catch { return new Set(); }
};

const saveLastSeen = (teamId, set) => {
  try {
    // Cap at 500 ids — far more than any team needs short-term.
    const arr = Array.from(set).slice(-500);
    localStorage.setItem(LAST_SEEN_KEY(teamId), JSON.stringify(arr));
  } catch { /* quota */ }
};

/**
 * Diff a fresh list of team to-do items against last-seen ids.
 * Fires one local notification per new item. Returns the updated id set
 * so the caller can persist it.
 */
export const diffAndNotifyTeamTodos = (teamId, items, currentUserId) => {
  if (!notifEnabled()) return;
  const lastSeen = loadLastSeen(teamId);
  const newOnes = items.filter(it => !lastSeen.has(it.id));
  const isFirstRun = lastSeen.size === 0;
  newOnes.forEach(it => lastSeen.add(it.id));
  saveLastSeen(teamId, lastSeen);
  if (isFirstRun) return;  // Don't spam on the very first poll after enabling
  newOnes.forEach((item) => {
    if (item.created_by === currentUserId) return;  // don't notify yourself
    const body = item.text.length > 80 ? `${item.text.slice(0, 77)}…` : item.text;
    const by = item.created_by_name ? ` (added by ${item.created_by_name})` : '';
    showLocalNotification('New team to-do', body + by, { tag: `todo-${item.id}`, todoId: item.id });
  });
};
